import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { useParams } from 'react-router-dom';
import { useFormik } from 'formik';
import { object, string } from 'yup';
import ReactECharts from 'echarts-for-react';

// Constants
import {
  DATA_RETRIEVAL_INTERVAL,
  INTERVAL_UPDATE_DATA,
  PAGE_TITLE,
  PAGE_TITLE_COMPONENT,
  TEXT_LENGTH_LIMITS,
} from '../../constants/common';

// Store
import { Dispatch, RootState } from '../../store/store';

// Types
import { ElementSize } from '../../types/element-size';
import { ButtonType } from '../../types/button-type';

// Enums
import CycleMeasurement from '../../enums/cycle-measurement';
import { Action } from '../../enums/action';

// Utils
import fahrenheitToCelsius from '../../utils/fahrenheitToCelsius';

// Components
import Modal from '../Modal/Modal';
import InteractiveButton from '../InteractiveButton/InteractiveButton';
import SpinnerLoader from '../SpinnerLoader/SpinnerLoader';
import TextAreaControl from '../TextAreaControl/TextAreaControl';
import SelectOption, { Option, SelectedValue } from '../SelectOption/SelectOption';
import { AbilityContext } from '../Can/Can';
import IconGear from '../Icons/IconGear/IconGear';
import IconMultipleLines from '../Icons/MultipleLines/MultipleLines';
import ChartPreferences from '../ChartPreferences/ChartPreferences';
import { DataZoomEvent } from '../CycleChartPreview/types';
import { useTitle } from '../../hooks/useTitle';
import { generateControllersName, generateCycleData, getNumberNoteSeries, sortNoteSeries } from './utils';
import { CycleController } from '../../store/models/controllers';
import getDeviceNamesUtil from '../../utils/getDeviceNames';
import getTempByMeasurement from '../../utils/getTempByMeasurement';
import getMeasurementPreference from '../../utils/getMeasurementPreference';
import { ChartPreference } from '../../store/models/chart-preference';
import applyYAxisPadding from '../../utils/applyYAxisPadding';
import { Note, NoteLink } from './types';
import { ICustomChannelName } from '../../types/channel-types';
import { NOTE_ID } from '../CycleChartPreview/constants';
import CycleChartPreviewV2 from '../CycleChartPreview/CycleChartPreviewV2';
import ReportChannelsModalV2 from '../ReportChannelsModal/ReportChannelsModalV2';
import GraphScreenshotV2 from '../GraphScreenshot/GraphScreenshotV2';
import { IoTDataResponse } from '../../store/models/cycles';
import { useTranslation } from 'react-i18next';

type ComponentProps = ReturnType<typeof mapDispatch> & ReturnType<typeof mapState>;

type NoeFormValues = {
  id: string | null;
  name: string | null;
  coordinate: { x: number | null; y: number | null };
};

const CycleChartV2: React.FC<ComponentProps> = ({
  cycleControllers,
  cycleIotData,
  cycleNotes,
  currentUserMeasurement,
  getCycle,
  deleteNote,
  createNote,
  updateNote,
  createNoteLink,
  deleteNoteLink,
  currentUserMember,
  updateViewNotes,
  chartPreference,
  getChartPreference,
  getChannels,
  updateCycleData,
  cycle,
  clearCycle,
  clearChartPreference,
  clearNote,
  clearChannels,
  getCustomChannelNames,
  getReportItems,
  cycleReportItems,
  updateReportItems,
}) => {
  const { clientId, contractId, screenId, id } = useParams();

  const { t } = useTranslation();
  const ability = useContext(AbilityContext);
  const IS_ACCESS = {
    NOTE: {
      CREATE: ability.can(Action.Create, PAGE_TITLE_COMPONENT[PAGE_TITLE.CYCLE].NOTE),
      DELETE: ability.can(Action.Delete, PAGE_TITLE_COMPONENT[PAGE_TITLE.CYCLE].NOTE),
      UPDATE: ability.can(Action.Update, PAGE_TITLE_COMPONENT[PAGE_TITLE.CYCLE].NOTE),
    },
  };
  const canEditCycle = useMemo(() => {
    return ability.can(Action.Read, PAGE_TITLE.EDIT_CYCLE);
  }, [ability]);

  const chartRef = useRef<ReactECharts | null>(null);

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isCreationNote, setIsCreationNote] = useState<boolean>(false);
  const [isEditNote, setIsEditNote] = useState<boolean>(false);
  const [isEditChartPreferences, setIsEditChartPreferences] = useState<boolean>(false);
  const [isEditReportChannels, setIsEditReportChannels] = useState<boolean>(false);
  const [isDeletionNote, setIsDeletionNote] = useState<boolean>(false);
  const [isDeletingChain, setIsDeletingChain] = useState<boolean>(false);
  const [linkTargetNoteId, setLinkTargetNoteId] = useState<string | null>(null);
  const [editingOccurrenceId, setEditingOccurrenceId] = useState<string>('root');
  const [linkingNoteId, setLinkingNoteId] = useState<string | null>(null);
  const linkingNoteIdRef = useRef<string | null>(null);
  const isDeletingChainRef = useRef<boolean>(false);
  const cycleRef = useRef(cycle);

  const [cycleComponents, setCycleComponent] = useState<any | null>(null);
  const [iotData, setIotData] = useState(null);
  const [cycleControllersName, setCycleControllersName] = useState<{ [key: string]: string } | null>(null);
  const [dataZoom, setDataZoom] = useState<DataZoomEvent | null>(null);
  const [cycleName, setCycleName] = useState<string>('undefined');
  const [nameDevices, setNameDevices] = useState<{ [key: string]: string } | null>(null);
  const [tempRange, setTempRange] = useState<Pick<ChartPreference, 'min_temperature' | 'max_temperature'> | null>(null);
  const [channelNames, setChannelNames] = useState<ICustomChannelName[] | null>(null);

  const isStopPullingIotData = isLoading || isCreationNote || isDeletionNote || isEditChartPreferences || isEditReportChannels || !!linkingNoteId;
  const isPullingIotData = chartPreference ? chartPreference.end_timestamp === null : false;

  useEffect(() => {
    linkingNoteIdRef.current = linkingNoteId;
  }, [linkingNoteId]);

  useEffect(() => {
    isDeletingChainRef.current = isDeletingChain;
  }, [isDeletingChain]);

  useEffect(() => {
    cycleRef.current = cycle;
  }, [cycle]);

  useTitle(cycleName);

  const generateNoteName = (name: string) => {
    return `${NOTE_ID}: ${name}`;
  }

  const onSubmitFormHandler = async (values: NoeFormValues) => {
    if (isCreationNote) {
      const { name, coordinate } = values;
      const formValued = { name, coordinate };

      if (currentUserMeasurement === CycleMeasurement.Fahrenheit) {
        formValued.coordinate.y = Number(fahrenheitToCelsius(formValued.coordinate.y));
      }

      if (linkTargetNoteId) {
        const response = await createNoteLink({
          noteId: linkTargetNoteId,
          payload: { coordinate: formValued.coordinate },
        });
        addLinkToListNotes(linkTargetNoteId, response);

        const updatedListNotes = cycleRef.current.notes.map((noteData) =>
          noteData.id === linkTargetNoteId
            ? { ...noteData, links: [...(noteData.links ?? []), response] }
            : noteData
        );
        updateCycleData({ notes: updatedListNotes });

        closeModalCreation();
        return;
      }

      const response = await createNote({ ...formValued, cycleId: id });
      expandListNotes(response);

      updateCycleData({ notes: [...cycleRef.current.notes, response] });

      closeModalCreation();
      return;
    }

    if (isDeletionNote) {
      const { id } = values;

      const response = await deleteNote({ id, deleteAll: isDeletingChainRef.current });

      if (response?.promoted) {
        promoteLinkInListNotes(id, response.promotedLinkId, response.coordinate);

        const updatedListNotes = cycleRef.current.notes.map((noteData) =>
          noteData.id === id
            ? {
                ...noteData,
                coordinate: response.coordinate,
                links: (noteData.links ?? []).filter((link) => link.id !== response.promotedLinkId),
              }
            : noteData
        );
        updateCycleData({ notes: updatedListNotes });

        closeModalEditing();
        return;
      }

      deleteNoteFromListNotes(id);

      const updatedListNotes = cycleRef.current.notes.filter((noteData) => noteData.id !== id);

      updateCycleData({ notes: updatedListNotes });

      closeModalEditing();

      // Confirmed (not just a stale-backend symptom): the incremental chart patch
      // above doesn't reliably remove a whole marker from the live chart — force a
      // full resync, matching what a manual refresh already does reliably.
      // NOTE: `id` here is the note's id (destructured from `values` above),
      // not the cycle's — use the cycle id via the ref to avoid that shadowing.
      await fetchData(cycleRef.current.id);
      return;
    }

    if (isEditNote) {
      const { name, id, coordinate } = values;
      const payload = { name };
      const response = await updateNote({
        id: values.id,
        payload,
      });

      const oldNoteData = { name, id, coordinate };
      updateListNotes(oldNoteData, response);

      const existingLinks = cycleRef.current.notes.find((noteData) => noteData.id === id)?.links;
      const updatedListNotes = cycleRef.current.notes.filter((noteData) => noteData.id !== id);
      updatedListNotes.push({ ...response, links: existingLinks });

      updateCycleData({ notes: updatedListNotes });


      closeModalEditing();
      return;
    }
  };

  const noteValidationSchema = useMemo(
    () =>
      object({
        name: linkTargetNoteId
          ? string().notRequired()
          : string()
              .min(TEXT_LENGTH_LIMITS.MIN, `Minimum ${TEXT_LENGTH_LIMITS.MIN} characters required`)
              .max(TEXT_LENGTH_LIMITS.MAX, `Maximum ${TEXT_LENGTH_LIMITS.MAX} characters required`)
              .required('Required'),
      }),
    [linkTargetNoteId],
  );

  const formik = useFormik<NoeFormValues>({
    initialValues: {
      id: null,
      name: '',
      coordinate: { x: null, y: null },
    },
    validationSchema: noteValidationSchema,
    onSubmit: onSubmitFormHandler,
  });

  const isDisabledNoteBtn = Boolean(formik.touched.name && formik.errors.name);

  const fetchData = async (id: string) => {
    const chartPreference = await getChartPreference(id);
    const from = chartPreference.start_timestamp;
    const to = chartPreference.end_timestamp === null ? new Date().getTime() : chartPreference.end_timestamp;

    await getReportItems(id);

    setIsLoading(true);
    const cycleData = await getCycle({
      id,
      query: {
        from: String(from),
        to: String(to),
      },
    });

    if (cycleData.name) {
      setCycleName(cycleData.name);
    }

    setIsLoading(false);

    const fetchChannelNames = async () => {
      const names = await getCustomChannelNames(id);
      setChannelNames(names);
    };
    fetchChannelNames();
  };

  const updateTempRange = (min: number, max: number) => {
    const convertedMinTemp = getTempByMeasurement(currentUserMeasurement, chartPreference.min_temperature);
    const convertedMaxTemp = getTempByMeasurement(currentUserMeasurement, chartPreference.max_temperature);

    const min_temperature = applyYAxisPadding(min, { min: convertedMinTemp, max: convertedMaxTemp }, 'min');
    const max_temperature = applyYAxisPadding(max, { min: convertedMinTemp, max: convertedMaxTemp }, 'max');

    setTempRange({ min_temperature, max_temperature });
  };

  const getDeviceNames = async (cycleControllers: CycleController[]) => {
    const promiseChannels = cycleControllers.map((item) => {
      return getChannels(item.controllerId);
    });

    const deviceNames = await getDeviceNamesUtil(cycleControllers, promiseChannels);
    setNameDevices(deviceNames);
  };

  const fetchNewData = async () => {
    const filterTo = new Date().getTime();
    const filterFrom = filterTo - DATA_RETRIEVAL_INTERVAL.TEN_MINUETS;
    await getCycle({
      id,
      query: { from: String(filterFrom), to: String(filterTo) },
    });
  };

  const createNoteByClick = async (event: any, chart: any) => {
    const x = event.offsetX;
    const y = event.offsetY;

    const dataCoords = chart.convertFromPixel({ seriesIndex: 0 }, [x, y]);

    const xValue = dataCoords[0];
    const yValue = dataCoords[1];

    if (linkingNoteIdRef.current) {
      const activeLinkingNoteId = linkingNoteIdRef.current;
      const coordinate = { x: xValue, y: yValue };

      if (currentUserMeasurement === CycleMeasurement.Fahrenheit) {
        coordinate.y = Number(fahrenheitToCelsius(coordinate.y));
      }

      const response = await createNoteLink({ noteId: activeLinkingNoteId, payload: { coordinate } });
      addLinkToListNotes(activeLinkingNoteId, response);

      const updatedListNotes = cycleRef.current.notes.map((noteData) =>
        noteData.id === activeLinkingNoteId
          ? { ...noteData, links: [...(noteData.links ?? []), response] }
          : noteData
      );
      updateCycleData({ notes: updatedListNotes });
      return;
    }

    formik.handleChange({
      target: {
        name: 'coordinate',
        value: { x: xValue, y: yValue },
      },
    });

    openModalCreation();
  };

  const expandListNotes = (note: Note) => {
    const formattedTime = new Date(note.coordinate.x);
    const convertedNoteCoordY = getTempByMeasurement(currentUserMeasurement, note.coordinate.y);

    const newNote = {
      name: generateNoteName(note.name),
      id: note.id,
      media: { coord: [formattedTime, convertedNoteCoordY] },
    };

    setCycleComponent((oldValue) => {
      const newNoteSeriesData = [...oldValue.noteSeriesData, newNote].sort((a, b) => a.media.coord[0] - b.media.coord[0]);

      const newSource = oldValue.source.map((item) => {
        if (item.time === note.coordinate.x) {
          return { ...item, [note.name]: convertedNoteCoordY };
        }

        return item;
      });

      return {
        ...oldValue,
        noteSeriesData: newNoteSeriesData,
        source: newSource,
      };
    });
  };

  const deleteNoteFromListNotes = (id: string) => {
    setCycleComponent((oldValue) => ({
      ...oldValue,
      noteSeriesData: oldValue.noteSeriesData.filter((note) => note.id !== id),
    }));
  };

  const addLinkToListNotes = (noteId: string, link: NoteLink) => {
    const convertedNoteCoordY = getTempByMeasurement(currentUserMeasurement, link.coordinate.y);
    const formattedTime = new Date(link.coordinate.x);

    setCycleComponent((oldValue) => {
      const newNoteSeriesData = oldValue.noteSeriesData.map((note) => {
        if (note.id !== noteId) {
          return note;
        }

        return {
          ...note,
          links: [...(note.links ?? []), { id: link.id, coord: [formattedTime, convertedNoteCoordY] as [Date, number] }],
        };
      });

      return {
        ...oldValue,
        noteSeriesData: newNoteSeriesData,
      };
    });
  };

  const removeLinkFromListNotes = (noteId: string, linkId: string) => {
    setCycleComponent((oldValue) => {
      const newNoteSeriesData = oldValue.noteSeriesData.map((note) => {
        if (note.id !== noteId) {
          return note;
        }

        return {
          ...note,
          links: (note.links ?? []).filter((link) => link.id !== linkId),
        };
      });

      return {
        ...oldValue,
        noteSeriesData: newNoteSeriesData,
      };
    });
  };

  const promoteLinkInListNotes = (noteId: string, promotedLinkId: string, newCoordinate: { x: number; y: number }) => {
    const convertedNoteCoordY = getTempByMeasurement(currentUserMeasurement, newCoordinate.y);
    const formattedTime = new Date(newCoordinate.x);

    setCycleComponent((oldValue) => {
      const newNoteSeriesData = oldValue.noteSeriesData.map((note) => {
        if (note.id !== noteId) {
          return note;
        }

        return {
          ...note,
          media: { coord: [formattedTime, convertedNoteCoordY] },
          links: (note.links ?? []).filter((link) => link.id !== promotedLinkId),
        };
      });

      return {
        ...oldValue,
        noteSeriesData: sortNoteSeries(newNoteSeriesData),
      };
    });
  };

  const updateListNotes = (oldNote: Note, updatedNote: Note) => {
    const convertedNoteCoordY = getTempByMeasurement(currentUserMeasurement, updatedNote.coordinate.y);
    const formattedTime = new Date(updatedNote.coordinate.x);

    setCycleComponent((oldValue) => {
      const newNoteSeriesData = oldValue.noteSeriesData.map((item) => {
        if (item.id === updatedNote.id) {
          return {
            name: generateNoteName(updatedNote.name),
            id: updatedNote.id,
            media: { coord: [formattedTime, convertedNoteCoordY] },
            links: item.links,
          };
        }

        return item;
      });

      const newSource = oldValue.source.map((item) => {
        if (item.time === formattedTime) {
          const newSourceItem = { ...item };
          delete newSourceItem[oldNote.name];

          newSourceItem[updatedNote.name] = convertedNoteCoordY;

          return newSourceItem;
        }

        return item;
      });

      return {
        ...oldValue,
        noteSeriesData: sortNoteSeries(newNoteSeriesData),
        source: newSource,
      };
    });
  };

  const openModalEditing = (data: Note, occurrenceId: string = 'root') => {
    formik.setValues({
      ...data,
    });

    setEditingOccurrenceId(occurrenceId);
    setIsEditNote(true);
  };

  const closeModalEditing = () => {
    formik.resetForm();
    setIsEditNote(false);
    setIsDeletionNote(false);
    setEditingOccurrenceId('root');
    setIsDeletingChain(false);
  };

  const openModalChartPreferences = () => {
    setIsEditChartPreferences(true);
  };

  const openModalReportChannels = () => {
    setIsEditReportChannels(true)
  };

  const closeModalReportChannels = () => {
    setIsEditReportChannels(false)
  };

  const closeModalChartPreferences = (isChangedValues: boolean = false) => {
    setIsEditChartPreferences(false);

    if (isChangedValues) {
      setIsLoading(true);
      fetchData(id);
    }
  };

  const openModalCreation = () => {
    setIsCreationNote(true);
  };

  const closeModalCreation = () => {
    formik.resetForm();
    setIsCreationNote(false);
    setLinkTargetNoteId(null);
  };

  const showDeleteNote = () => {
    setIsDeletionNote(true);
    setIsDeletingChain(false);
  };

  const showDeleteChain = () => {
    setIsDeletionNote(true);
    setIsDeletingChain(true);
  };

  const closeDeleteNote = () => {
    setIsDeletionNote(false);
    setIsDeletingChain(false);
  };

  const deleteLinkOccurrence = async (noteId: string, linkId: string) => {
    await deleteNoteLink({ noteId, linkId });

    removeLinkFromListNotes(noteId, linkId);

    const updatedListNotes = cycleRef.current.notes.map((noteData) =>
      noteData.id === noteId
        ? { ...noteData, links: (noteData.links ?? []).filter((link) => link.id !== linkId) }
        : noteData
    );
    updateCycleData({ notes: updatedListNotes });

    closeModalEditing();
  };

  const editNoteByClick = (params) => {
    params.event.event.stopPropagation();

    if (linkingNoteIdRef.current) {
      return;
    }

    if (params.componentType === 'markPoint' && formik.values.id === null) {
      setCycleComponent((oldValue) => {
        const { noteSeriesData } = oldValue;
        const note = noteSeriesData.find((note) => params.seriesId.includes(note.id));

        if (!note) {
          return oldValue;
        }

        const occurrenceId: string = params.data?.occurrenceId ?? 'root';
        openModalEditing(note, occurrenceId);

        return oldValue;
      });
    }
  };

  const linkNoteOptions: Option[] = useMemo(() => {
    if (!cycleComponents) return [];

    return cycleComponents.noteSeriesData.map((note, index) => ({
      id: note.id,
      value: `#${index + 1} ${note.name}`,
    }));
  }, [cycleComponents]);

  const selectedLinkOption: Option | null = useMemo(() => {
    if (!linkTargetNoteId) return null;

    return linkNoteOptions.find((option) => option.id === linkTargetNoteId) ?? null;
  }, [linkTargetNoteId, linkNoteOptions]);

  const linkTargetNote = useMemo(() => {
    return cycle?.notes?.find((note) => note.id === linkTargetNoteId) ?? null;
  }, [cycle, linkTargetNoteId]);

  const editingNoteHasLinks = useMemo(() => {
    return (cycleComponents?.noteSeriesData?.find((note) => note.id === formik.values.id)?.links?.length ?? 0) > 0;
  }, [cycleComponents, formik.values.id]);

  const selectLinkTarget = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedValue = event.target.value as unknown as SelectedValue;
    setLinkTargetNoteId(selectedValue.id === 'DEFAULT_OPTION' ? null : selectedValue.id);
  };

  const startLinkingMode = (noteId: string) => {
    setLinkingNoteId(noteId);
    closeModalEditing();
  };

  const stopLinkingMode = () => {
    setLinkingNoteId(null);
  };

  const changeViewNotes = () => {
    updateViewNotes({
      id: currentUserMember.id,
      is_show_notes: !currentUserMember.is_show_notes,
    });
  };

  const clearData = () => {
    clearCycle();
    clearChartPreference();
    clearNote();
    clearChannels();
  };

  useEffect(() => {
    fetchData(id);
    return () => {
      clearData();
    };
  }, [id]);

  useEffect(() => {
    if (isStopPullingIotData || !isPullingIotData) return;

    const intervalId = setInterval(() => {
      fetchNewData();
    }, INTERVAL_UPDATE_DATA);

    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [chartPreference, isCreationNote, isDeletionNote, isEditChartPreferences, cycleComponents, isLoading, isEditReportChannels]);

  useEffect(() => {
    if (cycleNotes && cycleIotData && currentUserMeasurement && chartPreference) {
      const { minTemp, maxTemp, ...restData } = generateCycleData(cycleNotes, cycleIotData as Record<string, IoTDataResponse[]>, currentUserMeasurement);

      setIotData(cycleIotData);
      setCycleComponent(restData);
      updateTempRange(minTemp, maxTemp);
    }
  }, [cycleNotes, cycleIotData, currentUserMeasurement]);

  useEffect(() => {
    if (cycleControllers) {
      const newCycleControllersName = generateControllersName(cycleControllers);

      getDeviceNames(cycleControllers);

      setCycleControllersName(newCycleControllersName);
    }
  }, [cycleControllers]);

  return (
    <>
      <div className="c-cycle-chart c-cycle-chart_v2 h-full w-full">
        {(isLoading || !chartPreference || iotData === null) && (
          <div className="c-cycle-chart__spinner flex h-full flex-auto items-center justify-center">
            <SpinnerLoader />
          </div>
        )}
        {!isLoading && chartPreference && iotData && (
          <>
            <div className="c-cycle-chart__info grid items-center pb-6 max-sm:grid-rows-[1fr_1fr_1fr_1fr] max-sm:gap-y-4 sm:h-[60px] sm:grid-cols-[auto_auto_1fr_auto] sm:gap-x-4">
              <label className="c-cycle-chart__note-tumbler flex h-full flex-row items-center rounded-md border border-primary px-2 py-1.5">
                <input
                  type="checkbox"
                  className="c-cycle-chart__note-tumbler-checkbox h-5 w-5 flex-auto"
                  onChange={changeViewNotes}
                  checked={currentUserMember.is_show_notes}
                />
                <span className="overflow-hidden text-ellipsis pl-2 text-sm">{t('note.commonText.showNoteText')}</span>
              </label>
              {canEditCycle && (
                <InteractiveButton
                  onClick={openModalReportChannels}
                  customType="max-h-fit max-w-fit border border-foggy-mist rounded active:border-primary cursor-pointer min-w-fit"
                  style={{ padding: ' 0px 12px' }}
                >
                  <IconMultipleLines stroke="inherit" iconClassName="text-inherit" size={ElementSize.MD} />
                </InteractiveButton>
              )}
              <div></div>
              <InteractiveButton
                onClick={openModalChartPreferences}
                customType="max-h-fit max-w-fit border border-foggy-mist rounded active:border-primary cursor-pointer min-w-fit"
                style={{ padding: ' 0px 12px' }}
              >
                <IconGear stroke="inherit" iconClassName="text-inherit" size={ElementSize.XS} />
              </InteractiveButton>
            </div>
            {linkingNoteId && cycleComponents && (
              <div className="mb-4 flex items-center justify-between rounded-md border border-primary bg-primary/10 px-4 py-2">
                <span className="text-sm">
                  {t('note.commonText.addingLinkedNotesFor')} #{getNumberNoteSeries(linkingNoteId, cycleComponents.noteSeriesData)}
                </span>
                <InteractiveButton
                  onClick={stopLinkingMode}
                  text={t('note.commonText.stopAdding')}
                  size={ElementSize.XS}
                  type={ButtonType.PrimaryInverse}
                />
              </div>
            )}
            {canEditCycle && cycleControllers && cycle.cycle_reports && (
              <GraphScreenshotV2
                chartRef={chartRef}
                clientId={clientId}
                contractId={contractId}
                cycleComponents={cycleComponents}
                cycleControllers={cycleControllers}
                cycleId={id}
                cycleReportItems={cycleReportItems}
                cycleReports={cycle.cycle_reports}
                dataZoom={dataZoom}
                screenId={screenId}
              />
            )}
            <div className="h-[calc(100%_-_234px)] w-full sm:h-[calc(100%_-_114px)]">
              <CycleChartPreviewV2
                chartPreference={chartPreference}
                chartRef={chartRef}
                createNote={createNoteByClick}
                cycleComponents={cycleComponents}
                cycleControllersName={cycleControllersName}
                cycleReportItems={cycleReportItems}
                editNote={editNoteByClick}
                generateNoteName={generateNoteName}
                isNoteView={currentUserMember.is_show_notes}
                measurement={currentUserMeasurement}
                nameDevices={nameDevices}
                setDataZoom={setDataZoom}
              />
            </div>
            {IS_ACCESS.NOTE.CREATE && (
              <Modal
                modalTitle="Add note"
                isVisible={isCreationNote}
                onClose={closeModalCreation}
                showAcceptBtn={false}
                showCancelBtn={false}
              >
                <form className="c-cycle-chart__modal flex h-full flex-col" onSubmit={formik.handleSubmit}>
                  {linkNoteOptions.length > 0 && (
                    <div className="pb-4">
                      <SelectOption
                        name="linkTargetNoteId"
                        title={t('note.commonText.linkToExistingNote')}
                        placeholder={t('note.commonText.linkToExistingNotePlaceholder')}
                        options={linkNoteOptions}
                        selectedOption={selectedLinkOption}
                        onChange={selectLinkTarget}
                      />
                    </div>
                  )}
                  {linkTargetNoteId ? (
                    <div className="c-cycle-chart__modal-linked-note-preview">
                      <p className="pb-1.5 text-sm font-medium 2xl:pb-2.5 2xl:text-base">
                        {t('note.commonText.linkedNoteText')}
                      </p>
                      <p className="text-sm 2xl:text-base">{linkTargetNote?.name}</p>
                    </div>
                  ) : (
                    <TextAreaControl
                      label={t('note.commonText.noteLabel')}
                      name="name"
                      placeholder={t('note.commonText.enterNote')}
                      value={formik.values.name}
                      customOnChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      showError={formik.touched.name && formik.errors.name}
                      errorText={formik.errors.name}
                      className="h-full resize-y max-sm:min-h-full max-sm:resize-none sm:max-h-[200px] lg:max-h-[430px]"
                    />
                  )}
                  <div className="c-cycle-chart__modal-actions flex w-full flex-row justify-between pt-4">
                    <InteractiveButton
                      onClick={closeModalCreation}
                      text={t('buttons.cancel')}
                      size={ElementSize.XL}
                      type={ButtonType.PrimaryInverse}
                    />
                    <InteractiveButton text={t('buttons.save')} size={ElementSize.XL} disabled={isDisabledNoteBtn} submit />
                  </div>
                </form>
              </Modal>
            )}
            <Modal
              modalTitle={`${t('note.commonText.editNote')} #${getNumberNoteSeries(formik.values.id, cycleComponents.noteSeriesData)}`}
              isVisible={isEditNote}
              onClose={closeModalEditing}
              showAcceptBtn={false}
              showCancelBtn={false}
            >
              <form className="c-cycle-chart__modal flex h-full flex-col" onSubmit={formik.handleSubmit}>
                {!isDeletionNote && (
                  <TextAreaControl
                    label={t('note.commonText.noteLabel')}
                    name="name"
                    placeholder={t('note.commonText.enterNote')}
                    value={formik.values.name}
                    customOnChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    showError={formik.touched.name && formik.errors.name}
                    errorText={formik.errors.name}
                    disabled={!IS_ACCESS.NOTE.DELETE || !IS_ACCESS.NOTE.UPDATE}
                    className="h-full resize-y max-sm:min-h-full max-sm:resize-none sm:max-h-[200px] lg:max-h-[430px]"
                  />
                )}
                {isDeletionNote && IS_ACCESS.NOTE.DELETE && (
                  <>
                    <div className="relative flex min-h-[152px] items-center justify-center">
                      <p className="pb-2 text-center text-xl">
                        {isDeletingChain ? t('note.commonText.confirmDeleteNoteChain') : t('note.commonText.confirmDeleteNote')}
                      </p>
                    </div>
                    <div className="c-cycle-chart__modal-actions flex w-full flex-row justify-between pt-4">
                      <InteractiveButton
                        onClick={closeDeleteNote}
                        text={t('buttons.cancel')}
                        size={ElementSize.XL}
                        type={ButtonType.PrimaryInverse}
                      />
                      <InteractiveButton text={t('buttons.delete')} size={ElementSize.XL} submit />
                    </div>
                  </>
                )}
                {!isDeletionNote && IS_ACCESS.NOTE.CREATE && (
                  <div className="c-cycle-chart__modal-actions flex w-full flex-row justify-between pt-4">
                    <InteractiveButton
                      onClick={() => startLinkingMode(formik.values.id)}
                      text={t('note.commonText.addLinkedNotes')}
                      size={ElementSize.XL}
                      type={ButtonType.PrimaryInverse}
                    />
                  </div>
                )}
                {!isDeletionNote && editingNoteHasLinks && IS_ACCESS.NOTE.DELETE && (
                  <div className="c-cycle-chart__modal-actions flex w-full flex-row justify-between pt-4">
                    <InteractiveButton
                      onClick={showDeleteChain}
                      text={t('note.commonText.deleteAllInChain')}
                      size={ElementSize.XL}
                      type={ButtonType.PrimaryInverse}
                    />
                  </div>
                )}
                {!isDeletionNote && IS_ACCESS.NOTE.UPDATE && (
                  <div className="c-cycle-chart__modal-actions flex w-full flex-row justify-between pt-4">
                    <InteractiveButton
                      onClick={editingOccurrenceId === 'root' ? showDeleteNote : () => deleteLinkOccurrence(formik.values.id, editingOccurrenceId)}
                      text={t('buttons.delete')}
                      size={ElementSize.XL}
                      type={ButtonType.PrimaryInverse}
                    />
                    <InteractiveButton text={t('buttons.save')} size={ElementSize.XL} disabled={isDisabledNoteBtn} submit />
                  </div>
                )}
              </form>
            </Modal>
            {isEditChartPreferences && chartPreference && (
              <ChartPreferences
                cycleId={id}
                isVisible={isEditChartPreferences}
                onOpen={openModalChartPreferences}
                onClose={closeModalChartPreferences}
                chartPreference={chartPreference}
                measurement={currentUserMeasurement}
                currentTempRange={tempRange}
              />
            )}
            {isEditReportChannels && cycleControllers && (
              <ReportChannelsModalV2
                channelNames={channelNames}
                cycleControllers={cycleControllers}
                fetchData={() => getReportItems(id)}
                onClose={closeModalReportChannels}
                updateData={(items) => updateReportItems({ cycleId: id, items })}
                cycleReportItems={cycleReportItems}
              />
            )}
          </>
        )}
      </div>
    </>
  );
};

const mapState = (state: RootState) => ({
  cycle: state.cycles.cycle,
  cycleControllers: state.cycles.cycle?.cycle_controllers,
  cycleIotData: state.cycles.cycle?.iotDataResponse,
  cycleNotes: state.cycles.cycle?.notes,
  currentUserMeasurement: getMeasurementPreference(state.contracts.contract, state.locations.currentLocation),
  currentUserMember: state.members.currentUserMember,
  chartPreference: state.chart_preference.chartPreference,
  cycleReportItems: state.reports.reportItems
});

const mapDispatch = (dispatch: Dispatch) => ({
  getCycle: dispatch.cycles.getChartCycleData,
  createNote: dispatch.note.createNote,
  deleteNote: dispatch.note.deleteNote,
  updateNote: dispatch.note.updateNote,
  createNoteLink: dispatch.note.createNoteLink,
  deleteNoteLink: dispatch.note.deleteNoteLink,
  updateViewNotes: dispatch.members.updateViewNotes,
  getChartPreference: dispatch.chart_preference.getOneByCycleId,
  clearCycle: dispatch.cycles.resetOne,
  clearChartPreference: dispatch.cycles.resetOne,
  clearNote: dispatch.note.resetState,
  clearChannels: dispatch.channels.resetChannelState,
  getChannels: dispatch.channels.getChannels,
  getExcludeChannelNames: dispatch.cycles.getExcludeChannelNames,
  getColorDevices: dispatch.cycles.getColorDevices,
  updateCycleData: dispatch.cycles.setOneData,
  getCustomChannelNames: dispatch.reports.getCustomChannelNames,
  getReportItems: dispatch.reports.getCycleReportItemsV2,
  updateReportItems: dispatch.reports.updateCycleReportItemsV2,
});

export default connect(mapState, mapDispatch)(CycleChartV2);
